 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createSyncMiddleware, WithSyncControls } from './middleware/syncMiddleware';
import type { GameDate } from '../types/events';
import type { DeviceInventory, AutoOrderSetting } from '@shared/types/inventory';
import { GAME_CONSTANTS } from '../config/gameConstants';

export interface ComponentInventory {
  componentId: string;
  componentName: string;
  category: string;
  quantity: number;
  unitCost: number;
  totalValue: number;
  minStockLevel: number;
  maxStockLevel: number;
  reorderPoint: number;
  reorderQuantity: number;
  lastUpdated: number;
  qualityScore: number;
  supplierId?: string;
}

export interface Supplier {
  id: string;
  name: string;
  reliability: number; // 0-1
  priceLevel: 'low' | 'medium' | 'high';
  leadTime: number; // days
  qualityRating: number; // 0-1
  components: {
    componentId: string;
    basePrice: number;
    volumeDiscounts: {
      quantity: number;
      discount: number;
    }[];
    availability: number; // units available
  }[];
  contractTerms?: {
    minimumOrder: number;
    paymentTerms: number; // days
    exclusivity: boolean;
    expiryDate: GameDate;
  };
  performanceHistory: {
    onTimeDelivery: number;
    qualityDefects: number;
    priceStability: number;
  };
}

export interface PurchaseOrder {
  id: string;
  supplierId: string;
  supplierName: string;
  orderDate: GameDate;
  expectedDelivery: GameDate;
  actualDelivery?: GameDate;
  status: 'pending' | 'confirmed' | 'in_transit' | 'delivered' | 'cancelled';
  items: {
    componentId: string;
    componentName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }[];
  totalAmount: number;
  paymentStatus: 'pending' | 'partial' | 'paid';
  notes?: string;
}

export interface SupplyChainEvent {
  id: string;
  type: 'shortage' | 'price_increase' | 'quality_issue' | 'delivery_delay' | 'new_supplier' | 'supplier_bankruptcy';
  severity: 'low' | 'medium' | 'high' | 'critical';
  affectedComponents: string[];
  affectedSuppliers: string[];
  description: string;
  impact: {
    costIncrease?: number;
    availabilityDecrease?: number;
    qualityDecrease?: number;
    deliveryDelay?: number;
  };
  startDate: GameDate;
  duration: number; // days
  resolved: boolean;
}

export interface ComponentCost {
  componentId: string;
  baseCost: number;
  currentCost: number;
  marketPrice: number;
  trend: 'increasing' | 'stable' | 'decreasing';
  volatility: number; // 0-1
  priceHistory: {
    date: GameDate;
    price: number;
  }[];
  forecastedPrice: number;
  factors: {
    supply: number;
    demand: number;
    technology: number;
    economy: number;
  };
}

export interface InventoryMetrics {
  totalValue: number;
  turnoverRate: number;
  carryingCost: number;
  stockoutRate: number;
  excessInventory: number;
  obsoleteInventory: number;
  averageLeadTime: number;
  fillRate: number;
  componentsByCategory: Map<string, number>;
  topComponentsByCost: { componentId: string; value: number }[];
}

// Helper functions for Map serialization
const mapToObject = <K extends string, V>(map: Map<K, V>): Record<string, V> => {
  const obj: Record<string, V> = {};
  map.forEach((value, key) => {
    obj[key] = value;
  });
  return obj;
};

const objectToMap = <K extends string, V>(obj: Record<string, V>): Map<K, V> => {
  const map = new Map<K, V>();
  Object.entries(obj).forEach(([key, value]) => {
    map.set(key as K, value);
  });
  return map;
};

interface InventoryStore extends WithSyncControls {
  // State
  components: Map<string, ComponentInventory>;
  devices: Map<string, DeviceInventory>;
  suppliers: Map<string, Supplier>;
  purchaseOrders: PurchaseOrder[];
  supplyChainEvents: SupplyChainEvent[];
  componentCosts: Map<string, ComponentCost>;
  metrics: InventoryMetrics;
  autoReorderEnabled: boolean;
  autoOrderSettings: Map<string, AutoOrderSetting>;
  
  // Basic Actions
  updateInventory: (componentId: string, quantity: number, operation: 'add' | 'remove' | 'set', metadata?: { name?: string; category?: string; unitCost?: number }) => void;
  updateComponentCost: (componentId: string, newCost: number) => void;
  loadComponents: (components: ComponentInventory[]) => void;
  addSupplier: (supplier: Supplier) => void;
  updateSupplier: (supplierId: string, updates: Partial<Supplier>) => void;
  removeSupplier: (supplierId: string) => void;
  
  // Device Actions
  updateDeviceInventory: (deviceId: string, quantity: number, operation: 'add' | 'remove' | 'set', metadata?: Partial<DeviceInventory>) => void;
  loadDevices: (devices: DeviceInventory[]) => void;
  
  // Purchase Order Actions
  createPurchaseOrder: (order: Omit<PurchaseOrder, 'id'> & { id?: string }) => string;
  updatePurchaseOrder: (orderId: string, updates: Partial<PurchaseOrder>) => void;
  cancelPurchaseOrder: (orderId: string) => void;
  receivePurchaseOrder: (orderId: string) => void;
  loadPurchaseOrders: (orders: PurchaseOrder[]) => void;
  
  // Supply Chain Event Actions
  addSupplyChainEvent: (event: SupplyChainEvent) => void;
  resolveSupplyChainEvent: (eventId: string) => void;
  processSupplyChainEvent: (event: SupplyChainEvent) => void;
  
  // Cost Management Actions
  updateComponentCosts: (costs: Map<string, ComponentCost>) => void;
  calculateComponentCost: (componentId: string, quantity: number, supplierId?: string) => number;
  forecastComponentCosts: (months: number) => Map<string, number>;
  
  // Inventory Management Actions
  checkStockLevels: () => { componentId: string; status: 'critical' | 'low' | 'optimal' | 'excess' }[];
  generateReorderSuggestions: () => { componentId: string; quantity: number; supplierId: string; urgency: 'high' | 'medium' | 'low' }[];
  processAutoReorders: () => void;
  optimizeInventoryLevels: () => void;
  
  // Analytics Actions
  calculateInventoryMetrics: () => void;
  getComponentAvailability: (componentId: string) => { available: number; incoming: number; allocated: number };
  getSupplierPerformance: (supplierId: string) => { reliability: number; quality: number; cost: number };
  
  // Complex Actions
  negotiateSupplierContract: (supplierId: string, terms: Partial<Supplier['contractTerms']>) => boolean;
  findAlternativeSuppliers: (componentId: string) => Supplier[];
  evaluateSupplyChainRisk: () => { riskLevel: number; vulnerabilities: string[] };
  
  // Selectors
  selectLowStockComponents: () => ComponentInventory[];
  selectPendingOrders: () => PurchaseOrder[];
  selectActiveSupplyChainEvents: () => SupplyChainEvent[];
  selectTopSuppliers: (limit?: number) => Supplier[];
  selectComponentsByCategory: (category: string) => ComponentInventory[];
  
  // Auto Order Settings Actions
  setAutoOrderSetting: (componentId: string, setting: AutoOrderSetting) => void;
  removeAutoOrderSetting: (componentId: string) => void;
  getAutoOrderSetting: (componentId: string) => AutoOrderSetting | undefined;

  // Reset
  resetInventory: () => void;

  // Hydration from save
  hydrateFromSave: (saveData: {
    components?: Array<{
      id: string;
      componentId: string;
      componentName: string;
      category: string;
      currentStock: number;
      minStock: number;
      maxStock: number;
      unitCost: number;
      consumptionRate: number;
      lastOrderDate?: Date;
      lastOrderQuantity?: number;
      leadTimeDays: number;
    }>;
    devices?: Array<DeviceInventory>;
    pendingOrders?: Array<{
      orderId: string;
      componentId: string;
      componentName: string;
      quantity: number;
      unitCost: number;
      totalCost: number;
      status: string;
      orderDate: Date;
      expectedDeliveryDate: Date;
      gameOrderDate?: { day: number; month: number; year: number };
      gameDeliveryDate?: { day: number; month: number; year: number };
    }>;
    autoOrderSettings?: Record<string, AutoOrderSetting>;
  } | undefined) => void;
}

const defaultMetrics: InventoryMetrics = {
  totalValue: 0,
  turnoverRate: 0,
  carryingCost: 0,
  stockoutRate: 0,
  excessInventory: 0,
  obsoleteInventory: 0,
  averageLeadTime: 0,
  fillRate: 0,
  componentsByCategory: new Map(),
  topComponentsByCost: [],
};

// Partialize function for selective persistence
const partializeInventoryStore = (state: InventoryStore) => {
  // Exclude action functions and temporary state
  const {
    // Exclude all action functions
    updateInventory: _updateInventory,
    updateComponentCost: _updateComponentCost,
    loadComponents: _loadComponents,
    loadDevices: _loadDevices,
    updateDeviceInventory: _updateDeviceInventory,
    addSupplier: _addSupplier,
    updateSupplier: _updateSupplier,
    removeSupplier: _removeSupplier,
    createPurchaseOrder: _createPurchaseOrder,
    updatePurchaseOrder: _updatePurchaseOrder,
    cancelPurchaseOrder: _cancelPurchaseOrder,
    receivePurchaseOrder: _receivePurchaseOrder,
    loadPurchaseOrders: _loadPurchaseOrders,
    addSupplyChainEvent: _addSupplyChainEvent,
    resolveSupplyChainEvent: _resolveSupplyChainEvent,
    processSupplyChainEvent: _processSupplyChainEvent,
    updateComponentCosts: _updateComponentCosts,
    calculateComponentCost: _calculateComponentCost,
    forecastComponentCosts: _forecastComponentCosts,
    checkStockLevels: _checkStockLevels,
    generateReorderSuggestions: _generateReorderSuggestions,
    processAutoReorders: _processAutoReorders,
    optimizeInventoryLevels: _optimizeInventoryLevels,
    calculateInventoryMetrics: _calculateInventoryMetrics,
    getComponentAvailability: _getComponentAvailability,
    getSupplierPerformance: _getSupplierPerformance,
    negotiateSupplierContract: _negotiateSupplierContract,
    findAlternativeSuppliers: _findAlternativeSuppliers,
    evaluateSupplyChainRisk: _evaluateSupplyChainRisk,
    // Exclude selectors
    selectLowStockComponents: _selectLowStockComponents,
    selectPendingOrders: _selectPendingOrders,
    selectActiveSupplyChainEvents: _selectActiveSupplyChainEvents,
    selectTopSuppliers: _selectTopSuppliers,
    selectComponentsByCategory: _selectComponentsByCategory,
    resetInventory: _resetInventory,
    sync: _sync,
    ...persistableState
  } = state;
  
  // Convert Maps to objects and optimize data
  return {
    components: mapToObject(persistableState.components),
    devices: mapToObject(persistableState.devices),
    suppliers: mapToObject(persistableState.suppliers),
    purchaseOrders: persistableState.purchaseOrders,
    supplyChainEvents: (persistableState.supplyChainEvents || []).filter(e => !e.resolved), // Only persist active events
    componentCosts: mapToObject(persistableState.componentCosts),
    metrics: persistableState.metrics,
    autoReorderEnabled: persistableState.autoReorderEnabled,
    autoOrderSettings: mapToObject(persistableState.autoOrderSettings)
  };
};

// Sync configuration for inventory store
const inventorySyncConfig = {
  routing: {
    rules: [
      // Components and suppliers use IndexedDB with relationship tracking
      {
        match: (path) => 
          path.includes('components') || 
          path.includes('suppliers'),
        target: 'indexedDB' as const,
        priority: 100
      },
      // Purchase orders use IndexedDB for relationship management
      {
        match: (path) => path.includes('purchaseOrders'),
        target: 'indexedDB' as const,
        priority: 90
      },
      // Critical stock levels trigger immediate sync
      {
        match: (path, value) => {
          if (path.includes('components') && value) {
            const component = value as ComponentInventory;
            return component.quantity <= component.minStockLevel;
          }
          return false;
        },
        target: 'both' as const, // Save to both for redundancy
        priority: 110
      },
      // Metrics and settings use localStorage
      {
        match: (path) => 
          path.includes('metrics') || 
          path.includes('autoReorderEnabled'),
        target: 'localStorage' as const,
        priority: 70
      }
    ]
  },
  behavior: {
    debounceDelay: 0, // Immediate sync for critical stock levels
    batchSize: 50, // Batch supplier updates
    enableAutoSync: true,
    syncOnReconnect: true
  },
  hooks: {
    beforeSync: (data) => {
      // If data is not a full state object (e.g., just a field being synced), return as-is
      if (!data || typeof data !== 'object' || !('components' in data || 'devices' in data)) {
        return data;
      }

      // The data coming in here is already the state from Zustand
      // We need to check if it's already partialized or not
      if (data.components instanceof Map) {
        // Data has Maps, needs to be partialized
        const partialized = partializeInventoryStore(data);
        
        // Check for low stock alerts before persisting
        if (partialized.components) {
          const lowStockItems = Object.values(partialized.components).filter(
            (c: ComponentInventory) => c.quantity <= c.minStockLevel
          );
          
          if (lowStockItems.length > 0) {
            // Log low stock items before persisting
            // console.warn('[InventoryStore] Low stock items detected:', 
            //   lowStockItems.map(item => 
            //     `${item.componentName} (${item.quantity}/${item.minStockLevel})`
            //   )
            // );
          }
        }
        
        return partialized;
      } else {
        // Data is already partialized (plain objects)
        // Check for low stock alerts before persisting
        if (data.components) {
          const lowStockItems = Object.values(data.components).filter(
            (c: ComponentInventory) => c.quantity <= c.minStockLevel
          );
          
          if (lowStockItems.length > 0) {
            // Log low stock items before persisting
            // console.warn('[InventoryStore] Low stock items detected:', 
            //   lowStockItems.map(item => 
            //     `${item.componentName} (${item.quantity}/${item.minStockLevel})`
            //   )
            // );
          }
        }
        
        return data;
      }
    },
    afterSync: (result) => {
      if (result.success && result.target === 'indexedDB') {
        // Track relationship sync
        // console.debug('[InventoryStore] Synced relationships to IndexedDB');
      }
    },
    onError: (error) => {
      console.error('[InventoryStore] Sync error:', error);
      // Sync health error recording is handled by syncMiddleware
    }
  },
  monitoring: {
    trackMetrics: true,
    reportInterval: 30000, // Report every 30 seconds for inventory tracking
    metricsCallback: (metrics) => {
      // Metrics are handled by syncMiddleware which has access to syncHealthService
      // console.debug('[InventoryStore] Sync metrics:', {
      //   lastSyncTime: metrics.lastSyncTime,
      //   syncDuration: metrics.averageSyncTime,
      //   pendingChanges: metrics.pendingItems,
      //   failureRate: metrics.failedSyncs / (metrics.totalSyncs || 1)
      // });
    }
  }
};

export const useInventoryStore = create<InventoryStore>()(
  devtools(
    createSyncMiddleware(
      'inventoryStore',
      inventorySyncConfig
    )(
      (set, get) => ({
      // Initial state
      components: new Map(),
      devices: new Map(),
      suppliers: new Map(),
      purchaseOrders: [],
      supplyChainEvents: [],
      componentCosts: new Map(),
      metrics: defaultMetrics,
      autoReorderEnabled: true,
      autoOrderSettings: new Map(),

      // Basic Actions
      updateInventory: (componentId, quantity, operation, metadata) =>
        set(
          (state) => {
            const components = new Map(state.components);
            const existing = components.get(componentId);
            
            if (!existing && operation !== 'set') {
              // For 'add' operation, create a basic component entry if it doesn't exist
              if (operation === 'add') {
                console.warn(`Component ${componentId} not found in inventory, creating new entry`);
                const unitCost = metadata?.unitCost || 0;
                components.set(componentId, {
                  componentId,
                  componentName: metadata?.name || componentId,
                  category: metadata?.category || 'unknown',
                  quantity: quantity,
                  reorderPoint: 100,
                  minStockLevel: GAME_CONSTANTS.inventory.components.minStockLevel,
                  maxStockLevel: GAME_CONSTANTS.inventory.components.maxStockLevel,
                  reorderQuantity: 500,
                  qualityScore: metadata?.qualityScore || 1.0,
                  unitCost: unitCost,
                  totalValue: quantity * unitCost,
                  lastUpdated: Date.now(),
                });
                return { components };
              } else {
                console.error(`Component ${componentId} not found in inventory`);
                return state;
              }
            }
            
            let newQuantity = 0;
            if (operation === 'add') {
              newQuantity = (existing?.quantity || 0) + quantity;
            } else if (operation === 'remove') {
              newQuantity = Math.max(0, (existing?.quantity || 0) - quantity);
            } else {
              newQuantity = quantity;
            }
            
            if (existing) {
              components.set(componentId, {
                ...existing,
                quantity: newQuantity,
                totalValue: newQuantity * existing.unitCost,
                lastUpdated: Date.now(),
              });
            } else if (operation === 'set') {
              // Create new component entry for 'set' operation
              const unitCost = metadata?.unitCost || 0;
              components.set(componentId, {
                componentId,
                componentName: metadata?.name || componentId,
                category: metadata?.category || 'unknown',
                quantity: newQuantity,
                reorderPoint: 100,
                minStockLevel: GAME_CONSTANTS.inventory.components.minStockLevel,
                maxStockLevel: GAME_CONSTANTS.inventory.components.maxStockLevel,
                reorderQuantity: 500,
                qualityScore: metadata?.qualityScore || 1.0,
                unitCost: unitCost,
                totalValue: newQuantity * unitCost,
                lastUpdated: Date.now(),
              });
            }
            
            return { components };
          },
          false,
          'updateInventory'
        ),

      updateComponentCost: (componentId, newCost) =>
        set(
          (state) => {
            const components = new Map(state.components);
            const componentCosts = new Map(state.componentCosts);
            const existing = components.get(componentId);
            
            if (existing) {
              components.set(componentId, {
                ...existing,
                unitCost: newCost,
                totalValue: existing.quantity * newCost,
                lastUpdated: Date.now(),
              });
            }
            
            const costData = componentCosts.get(componentId);
            if (costData) {
              componentCosts.set(componentId, {
                ...costData,
                currentCost: newCost,
                trend: newCost > costData.currentCost ? 'increasing' : newCost < costData.currentCost ? 'decreasing' : 'stable',
              });
            }
            
            return { components, componentCosts };
          },
          false,
          'updateComponentCost'
        ),

      addSupplier: (supplier) =>
        set(
          (state) => {
            const suppliers = new Map(state.suppliers);
            suppliers.set(supplier.id, supplier);
            return { suppliers };
          },
          false,
          'addSupplier'
        ),

      updateSupplier: (supplierId, updates) =>
        set(
          (state) => {
            const suppliers = new Map(state.suppliers);
            const existing = suppliers.get(supplierId);
            if (existing) {
              suppliers.set(supplierId, { ...existing, ...updates });
            }
            return { suppliers };
          },
          false,
          'updateSupplier'
        ),

      removeSupplier: (supplierId) =>
        set(
          (state) => {
            const suppliers = new Map(state.suppliers);
            suppliers.delete(supplierId);
            return { suppliers };
          },
          false,
          'removeSupplier'
        ),

      // Device Actions
      updateDeviceInventory: (deviceId, quantity, operation, metadata) =>
        set(
          (state) => {
            const devices = new Map(state.devices);
            const existing = devices.get(deviceId);
            
            // console.log('[InventoryStore] 📱 updateDeviceInventory called:', {
            //   deviceId,
            //   quantity,
            //   operation,
            //   hasExisting: !!existing,
            //   existingStock: existing?.currentStock || 0,
            //   metadata
            // });
            
            if (!existing && operation !== 'set') {
              console.error(`Device ${deviceId} not found in inventory`);
              return state;
            }
            
            let newQuantity = 0;
            if (operation === 'add') {
              newQuantity = (existing?.currentStock || 0) + quantity;
            } else if (operation === 'remove') {
              newQuantity = Math.max(0, (existing?.currentStock || 0) - quantity);
            } else {
              newQuantity = quantity;
            }
            
            // console.log('[InventoryStore] 📊 Device quantity calculation:', {
            //   deviceId,
            //   previousStock: existing?.currentStock || 0,
            //   operation,
            //   changeAmount: quantity,
            //   newQuantity
            // });
            
            if (existing) {
              devices.set(deviceId, {
                ...existing,
                ...metadata,
                currentStock: newQuantity,
                inventoryValue: newQuantity * (metadata?.unitValue || existing.unitValue || 0),
              });
            } else if (operation === 'set' && metadata) {
              // Create new device entry for 'set' operation with metadata
              devices.set(deviceId, {
                id: deviceId,
                productId: metadata.productId || deviceId,
                productName: metadata.productName || 'Unknown Device',
                currentStock: newQuantity,
                productionRate: metadata.productionRate || 0,
                targetStock: metadata.targetStock || 100,
                qualityScore: metadata.qualityScore || 0.8,
                unitValue: metadata.unitValue || 0,
                inventoryValue: newQuantity * (metadata.unitValue || 0),
                ...metadata,
              });
            }
            
            // console.log('[InventoryStore] ✅ Device inventory updated:', {
            //   deviceId,
            //   finalStock: devices.get(deviceId)?.currentStock,
            //   totalDevices: devices.size
            // });
            
            return { devices };
          },
          false,
          'updateDeviceInventory'
        ),

      loadDevices: (devices) =>
        set(
          () => {
            // console.log('[InventoryStore] 🔄 loadDevices called with:', {
            //   deviceCount: devices.length,
            //   devices: devices.map(d => ({
            //     id: d.id,
            //     productId: d.productId,
            //     productName: d.productName,
            //     currentStock: d.currentStock
            //   }))
            // });

            const devicesMap = new Map<string, DeviceInventory>();
            devices.forEach(device => {
              // Use productId as the key, not id
              devicesMap.set(device.productId, device);
              // console.log(`[InventoryStore] 📥 Loaded device: ${device.productName} (${device.productId}) with ${device.currentStock} units`);
            });

            // console.log('[InventoryStore] ✅ Devices loaded:', {
            //   totalDevices: devicesMap.size,
            //   deviceIds: Array.from(devicesMap.keys())
            // });

            return { devices: devicesMap };
          },
          false,
          'loadDevices'
        ),

      loadComponents: (componentsList) =>
        set(
          () => {
            const componentsMap = new Map<string, ComponentInventory>();
            componentsList.forEach(component => {
              componentsMap.set(component.componentId, component);
            });
            return { components: componentsMap };
          },
          false,
          'loadComponents'
        ),

      // Purchase Order Actions
      createPurchaseOrder: (order) => {
        const orderId = order.id || `PO-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        set(
          (state) => ({
            purchaseOrders: [...state.purchaseOrders, { ...order, id: orderId }],
          }),
          false,
          'createPurchaseOrder'
        );
        return orderId;
      },

      updatePurchaseOrder: (orderId, updates) =>
        set(
          (state) => ({
            purchaseOrders: state.purchaseOrders.map(order =>
              order.id === orderId ? { ...order, ...updates } : order
            ),
          }),
          false,
          'updatePurchaseOrder'
        ),

      cancelPurchaseOrder: (orderId) =>
        set(
          (state) => ({
            purchaseOrders: state.purchaseOrders.map(order =>
              order.id === orderId ? { ...order, status: 'cancelled' } : order
            ),
          }),
          false,
          'cancelPurchaseOrder'
        ),

      receivePurchaseOrder: (orderId) => {
        const state = get();
        const order = state.purchaseOrders.find(o => o.id === orderId);
        
        if (!order || order.status === 'delivered') {
          return;
        }
        
        // Update inventory
        order.items.forEach(item => {
          state.updateInventory(item.componentId, item.quantity, 'add', {
            name: item.componentName
          });
        });
        
        // Update order status
        state.updatePurchaseOrder(orderId, {
          status: 'delivered',
          actualDelivery: state.purchaseOrders[0]?.orderDate || { day: 1, month: 1, year: 2007 },
        });
      },

      loadPurchaseOrders: (orders) =>
        set(
          (state) => ({
            purchaseOrders: orders,
          }),
          false,
          'loadPurchaseOrders'
        ),

      // Supply Chain Event Actions
      addSupplyChainEvent: (event) =>
        set(
          (state) => ({
            supplyChainEvents: [...state.supplyChainEvents, event],
          }),
          false,
          'addSupplyChainEvent'
        ),

      resolveSupplyChainEvent: (eventId) =>
        set(
          (state) => ({
            supplyChainEvents: state.supplyChainEvents.map(event =>
              event.id === eventId ? { ...event, resolved: true } : event
            ),
          }),
          false,
          'resolveSupplyChainEvent'
        ),

      processSupplyChainEvent: (event) => {
        const state = get();
        
        // Apply event impacts
        if (event.impact.costIncrease) {
          event.affectedComponents.forEach(componentId => {
            const cost = state.componentCosts.get(componentId);
            if (cost) {
              state.updateComponentCost(
                componentId,
                cost.currentCost * (1 + event.impact.costIncrease!)
              );
            }
          });
        }
        
        // Add the event
        state.addSupplyChainEvent(event);
      },

      // Cost Management Actions
      updateComponentCosts: (costs) =>
        set(() => ({ componentCosts: costs }), false, 'updateComponentCosts'),

      calculateComponentCost: (componentId, quantity, supplierId) => {
        const state = get();
        const baseCost = state.componentCosts.get(componentId)?.currentCost || 0;
        
        if (!supplierId) {
          return baseCost * quantity;
        }
        
        const supplier = state.suppliers.get(supplierId);
        const component = supplier?.components.find(c => c.componentId === componentId);
        
        if (!component) {
          return baseCost * quantity;
        }
        
        // Apply volume discounts
        let discount = 0;
        component.volumeDiscounts.forEach(vd => {
          if (quantity >= vd.quantity) {
            discount = Math.max(discount, vd.discount);
          }
        });
        
        return component.basePrice * quantity * (1 - discount);
      },

      forecastComponentCosts: (months) => {
        const state = get();
        const forecasts = new Map<string, number>();
        
        state.componentCosts.forEach((cost, componentId) => {
          // Simple linear forecast based on trend
          let forecastedPrice = cost.currentCost;
          
          if (cost.trend === 'increasing') {
            forecastedPrice *= Math.pow(1.02, months); // 2% increase per month
          } else if (cost.trend === 'decreasing') {
            forecastedPrice *= Math.pow(0.98, months); // 2% decrease per month
          }
          
          // Apply volatility
          forecastedPrice *= (1 + (Math.random() - 0.5) * cost.volatility * 0.1);
          
          forecasts.set(componentId, forecastedPrice);
        });
        
        return forecasts;
      },

      // Inventory Management Actions
      checkStockLevels: () => {
        const state = get();
        const levels: { componentId: string; status: 'critical' | 'low' | 'optimal' | 'excess' }[] = [];
        
        state.components.forEach((component, componentId) => {
          let status: 'critical' | 'low' | 'optimal' | 'excess';
          
          if (component.quantity === 0) {
            status = 'critical';
          } else if (component.quantity < component.minStockLevel) {
            status = 'low';
          } else if (component.quantity > component.maxStockLevel) {
            status = 'excess';
          } else {
            status = 'optimal';
          }
          
          levels.push({ componentId, status });
        });
        
        return levels;
      },

      generateReorderSuggestions: () => {
        const state = get();
        const suggestions: { componentId: string; quantity: number; supplierId: string; urgency: 'high' | 'medium' | 'low' }[] = [];
        
        state.components.forEach((component, componentId) => {
          if (component.quantity <= component.reorderPoint) {
            // Find best supplier
            let bestSupplier: Supplier | null = null;
            let bestScore = -1;
            
            state.suppliers.forEach(supplier => {
              const hasComponent = supplier.components.some(c => c.componentId === componentId);
              if (hasComponent) {
                const score = supplier.reliability * 0.4 + supplier.qualityRating * 0.4 + (1 - (supplier.priceLevel === 'high' ? 1 : supplier.priceLevel === 'medium' ? 0.5 : 0)) * 0.2;
                if (score > bestScore) {
                  bestScore = score;
                  bestSupplier = supplier;
                }
              }
            });
            
            if (bestSupplier) {
              const urgency = component.quantity === 0 ? 'high' : component.quantity < component.minStockLevel * 0.5 ? 'medium' : 'low';
              
              suggestions.push({
                componentId,
                quantity: component.reorderQuantity,
                supplierId: bestSupplier.id,
                urgency,
              });
            }
          }
        });
        
        return suggestions.sort((a, b) => {
          const urgencyOrder = { high: 0, medium: 1, low: 2 };
          return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
        });
      },

      processAutoReorders: () => {
        const state = get();
        
        if (!state.autoReorderEnabled) {
          return;
        }
        
        const suggestions = state.generateReorderSuggestions();
        
        suggestions.forEach(suggestion => {
          if (suggestion.urgency === 'high') {
            const supplier = state.suppliers.get(suggestion.supplierId);
            if (supplier) {
              const component = state.components.get(suggestion.componentId);
              if (component) {
                const cost = state.calculateComponentCost(
                  suggestion.componentId,
                  suggestion.quantity,
                  suggestion.supplierId
                );
                
                state.createPurchaseOrder({
                  supplierId: suggestion.supplierId,
                  supplierName: supplier.name,
                  orderDate: { day: 1, month: 1, year: 2007 }, // Should use actual game date
                  expectedDelivery: { day: 1 + supplier.leadTime, month: 1, year: 2007 },
                  status: 'pending',
                  items: [{
                    componentId: suggestion.componentId,
                    componentName: component.componentName,
                    quantity: suggestion.quantity,
                    unitPrice: cost / suggestion.quantity,
                    totalPrice: cost,
                  }],
                  totalAmount: cost,
                  paymentStatus: 'pending',
                  notes: 'Auto-reorder: Critical stock level',
                });
              }
            }
          }
        });
      },

      optimizeInventoryLevels: () => {
        const state = get();
        
        state.components.forEach((component, componentId) => {
          // Calculate optimal levels based on usage patterns
          // This is a simplified version - real implementation would use historical data
          const optimalMin = component.reorderPoint * 1.2;
          const optimalMax = component.reorderQuantity * 2;
          const optimalReorder = component.reorderQuantity;
          
          const components = new Map(state.components);
          components.set(componentId, {
            ...component,
            minStockLevel: Math.round(optimalMin),
            maxStockLevel: Math.round(optimalMax),
            reorderQuantity: Math.round(optimalReorder),
          });
          
          set({ components }, false, 'optimizeInventoryLevels');
        });
      },

      // Analytics Actions
      calculateInventoryMetrics: () => {
        const state = get();
        let totalValue = 0;
        const componentsByCategory = new Map<string, number>();
        const componentValues: { componentId: string; value: number }[] = [];
        
        state.components.forEach((component, componentId) => {
          totalValue += component.totalValue;
          
          const categoryCount = componentsByCategory.get(component.category) || 0;
          componentsByCategory.set(component.category, categoryCount + component.quantity);
          
          componentValues.push({ componentId, value: component.totalValue });
        });
        
        const topComponentsByCost = componentValues
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);
        
        const metrics: InventoryMetrics = {
          totalValue,
          turnoverRate: 0, // Would need sales data to calculate
          carryingCost: totalValue * 0.25 / 12, // Assuming 25% annual carrying cost
          stockoutRate: state.components.size > 0 
            ? Array.from(state.components.values()).filter(c => c.quantity === 0).length / state.components.size 
            : 0,
          excessInventory: Array.from(state.components.values())
            .filter(c => c.quantity > c.maxStockLevel)
            .reduce((sum, c) => sum + (c.quantity - c.maxStockLevel) * c.unitCost, 0),
          obsoleteInventory: 0, // Would need age tracking
          averageLeadTime: state.suppliers.size > 0
            ? Array.from(state.suppliers.values()).reduce((sum, s) => sum + s.leadTime, 0) / state.suppliers.size
            : 0,
          fillRate: 1 - (state.stockoutRate || 0),
          componentsByCategory,
          topComponentsByCost,
        };
        
        set({ metrics }, false, 'calculateInventoryMetrics');
      },

      getComponentAvailability: (componentId) => {
        const state = get();
        const component = state.components.get(componentId);
        const available = component?.quantity || 0;
        
        // Calculate incoming from purchase orders
        const incoming = state.purchaseOrders
          .filter(po => po.status === 'confirmed' || po.status === 'in_transit')
          .reduce((sum, po) => {
            const item = po.items.find(i => i.componentId === componentId);
            return sum + (item?.quantity || 0);
          }, 0);
        
        // Would need production data to calculate allocated
        const allocated = 0;
        
        return { available, incoming, allocated };
      },

      getSupplierPerformance: (supplierId) => {
        const state = get();
        const supplier = state.suppliers.get(supplierId);
        
        if (!supplier) {
          return { reliability: 0, quality: 0, cost: 0 };
        }
        
        // Calculate performance metrics
        const reliability = supplier.performanceHistory.onTimeDelivery;
        const quality = 1 - supplier.performanceHistory.qualityDefects;
        const cost = supplier.priceLevel === 'low' ? 1 : supplier.priceLevel === 'medium' ? 0.7 : 0.4;
        
        return { reliability, quality, cost };
      },

      // Complex Actions
      negotiateSupplierContract: (supplierId, terms) => {
        const state = get();
        const supplier = state.suppliers.get(supplierId);
        
        if (!supplier) {
          return false;
        }
        
        // Simple negotiation logic
        const acceptanceProbability = supplier.reliability * 0.5 + Math.random() * 0.5;
        
        if (acceptanceProbability > 0.6) {
          state.updateSupplier(supplierId, {
            contractTerms: terms,
          });
          return true;
        }
        
        return false;
      },

      findAlternativeSuppliers: (componentId) => {
        const state = get();
        const alternatives: Supplier[] = [];
        
        state.suppliers.forEach(supplier => {
          const hasComponent = supplier.components.some(c => c.componentId === componentId);
          if (hasComponent) {
            alternatives.push(supplier);
          }
        });
        
        return alternatives.sort((a, b) => {
          const aScore = a.reliability * 0.4 + a.qualityRating * 0.4 + (1 - (a.priceLevel === 'high' ? 1 : a.priceLevel === 'medium' ? 0.5 : 0)) * 0.2;
          const bScore = b.reliability * 0.4 + b.qualityRating * 0.4 + (1 - (b.priceLevel === 'high' ? 1 : b.priceLevel === 'medium' ? 0.5 : 0)) * 0.2;
          return bScore - aScore;
        });
      },

      evaluateSupplyChainRisk: () => {
        const state = get();
        let riskLevel = 0;
        const vulnerabilities: string[] = [];
        
        // Check supplier concentration
        const supplierCount = state.suppliers.size;
        if (supplierCount < 3) {
          riskLevel += 0.3;
          vulnerabilities.push('Low supplier diversity');
        }
        
        // Check stock levels
        const stockLevels = state.checkStockLevels();
        const criticalCount = stockLevels.filter(s => s.status === 'critical').length;
        if (criticalCount > 0) {
          riskLevel += 0.2 * (criticalCount / stockLevels.length);
          vulnerabilities.push(`${criticalCount} components at critical stock levels`);
        }
        
        // Check active supply chain events
        const activeEvents = state.supplyChainEvents.filter(e => !e.resolved);
        if (activeEvents.length > 0) {
          riskLevel += 0.1 * activeEvents.length;
          vulnerabilities.push(`${activeEvents.length} active supply chain disruptions`);
        }
        
        // Check supplier reliability
        const avgReliability = state.suppliers.size > 0
          ? Array.from(state.suppliers.values()).reduce((sum, s) => sum + s.reliability, 0) / state.suppliers.size
          : 0;
        if (avgReliability < 0.7) {
          riskLevel += 0.2;
          vulnerabilities.push('Low average supplier reliability');
        }
        
        return { riskLevel: Math.min(1, riskLevel), vulnerabilities };
      },

      // Selectors
      selectLowStockComponents: () => {
        const state = get();
        return Array.from(state.components.values())
          .filter(c => c.quantity <= c.reorderPoint)
          .sort((a, b) => a.quantity - b.quantity);
      },

      selectPendingOrders: () => {
        const state = get();
        return state.purchaseOrders.filter(po => 
          po.status === 'pending' || po.status === 'confirmed' || po.status === 'in_transit'
        );
      },

      selectActiveSupplyChainEvents: () => {
        const state = get();
        return state.supplyChainEvents.filter(e => !e.resolved);
      },

      selectTopSuppliers: (limit = 5) => {
        const state = get();
        return Array.from(state.suppliers.values())
          .sort((a, b) => {
            const aScore = a.reliability * 0.4 + a.qualityRating * 0.4 + (1 - (a.priceLevel === 'high' ? 1 : a.priceLevel === 'medium' ? 0.5 : 0)) * 0.2;
            const bScore = b.reliability * 0.4 + b.qualityRating * 0.4 + (1 - (b.priceLevel === 'high' ? 1 : b.priceLevel === 'medium' ? 0.5 : 0)) * 0.2;
            return bScore - aScore;
          })
          .slice(0, limit);
      },

      selectComponentsByCategory: (category) => {
        const state = get();
        return Array.from(state.components.values())
          .filter(c => c.category === category);
      },

      // Auto Order Settings Actions
      setAutoOrderSetting: (componentId, setting) =>
        set(
          (state) => {
            const autoOrderSettings = new Map(state.autoOrderSettings);

            // Migrate legacy settings with orderOnZeroInventory to autoOrderThreshold
            let migratedSetting = setting;
            if (setting.autoOrderThreshold === undefined) {
              // Legacy setting - convert orderOnZeroInventory to autoOrderThreshold
              const legacySetting = setting as AutoOrderSetting & { orderOnZeroInventory?: boolean };
              migratedSetting = {
                ...setting,
                autoOrderThreshold: legacySetting.orderOnZeroInventory ? 0 : null,
              };
            }

            autoOrderSettings.set(componentId, migratedSetting);
            return { autoOrderSettings };
          },
          false,
          'setAutoOrderSetting'
        ),

      removeAutoOrderSetting: (componentId) =>
        set(
          (state) => {
            const autoOrderSettings = new Map(state.autoOrderSettings);
            autoOrderSettings.delete(componentId);
            return { autoOrderSettings };
          },
          false,
          'removeAutoOrderSetting'
        ),

      getAutoOrderSetting: (componentId) => {
        const state = get();
        return state.autoOrderSettings.get(componentId);
      },

      // Reset
      resetInventory: () =>
        set(
          {
            components: new Map(),
            devices: new Map(),
            suppliers: new Map(),
            purchaseOrders: [],
            supplyChainEvents: [],
            componentCosts: new Map(),
            metrics: defaultMetrics,
            autoReorderEnabled: true,
            autoOrderSettings: new Map(),
          },
          false,
          'resetInventory'
        ),

      // Hydration from save
      hydrateFromSave: (saveData) =>
        set(
          (state) => {
            if (!saveData) return state;

            // Convert components array to Map
            const components = new Map<string, ComponentInventory>();
            (saveData.components ?? []).forEach(comp => {
              const inventory: ComponentInventory = {
                componentId: comp.componentId,
                componentName: comp.componentName,
                category: comp.category,
                quantity: comp.currentStock,
                unitCost: comp.unitCost,
                totalValue: comp.currentStock * comp.unitCost,
                minStockLevel: comp.minStock,
                maxStockLevel: comp.maxStock,
                reorderPoint: comp.minStock,
                reorderQuantity: comp.maxStock - comp.minStock,
                lastUpdated: Date.now(),
                qualityScore: 1.0,
              };
              components.set(comp.componentId, inventory);
            });

            // Convert devices array to Map
            const devices = new Map<string, DeviceInventory>();
            (saveData.devices ?? []).forEach(device => {
              devices.set(device.productId, device);
            });

            // Convert pending orders to purchase orders
            const purchaseOrders: PurchaseOrder[] = (saveData.pendingOrders ?? []).map(order => ({
              id: order.orderId,
              supplierId: '',
              supplierName: order.componentName,
              orderDate: order.gameOrderDate ?? { day: 1, month: 1, year: 2007 },
              expectedDelivery: order.gameDeliveryDate ?? { day: 1, month: 1, year: 2007 },
              status: order.status as PurchaseOrder['status'],
              items: [{
                componentId: order.componentId,
                componentName: order.componentName,
                quantity: order.quantity,
                unitPrice: order.unitCost,
                totalPrice: order.totalCost,
              }],
              totalAmount: order.totalCost,
              paymentStatus: 'pending' as const,
            }));

            // Convert autoOrderSettings object to Map
            const autoOrderSettings = new Map<string, AutoOrderSetting>();
            if (saveData.autoOrderSettings) {
              Object.entries(saveData.autoOrderSettings).forEach(([id, setting]) => {
                autoOrderSettings.set(id, setting);
              });
            }

            return {
              components,
              devices,
              purchaseOrders,
              suppliers: state.suppliers,
              supplyChainEvents: [],
              componentCosts: new Map(),
              metrics: defaultMetrics,
              autoReorderEnabled: true,
              autoOrderSettings,
            };
          },
          false,
          'hydrateFromSave'
        ),
      }),
    ),
    {
      name: 'inventory-store',
    }
  )
);

// Selectors
export const selectComponents = (state: InventoryStore) => state.components;
export const selectDevices = (state: InventoryStore) => state.devices;
export const selectSuppliers = (state: InventoryStore) => state.suppliers;
export const selectPurchaseOrders = (state: InventoryStore) => state.purchaseOrders;
export const selectSupplyChainEvents = (state: InventoryStore) => state.supplyChainEvents;
export const selectComponentCosts = (state: InventoryStore) => state.componentCosts;
export const selectInventoryMetrics = (state: InventoryStore) => state.metrics;